/**
 * AskBaskyDrawer — 480px slide-over from the right. Chat UI wired to
 * Claude Sonnet 4.5 via POST /api/ai/ask (Emergent LLM Key).
 *
 *  - Welcome card + 5 context-aware starter prompts (from askBaskyKnowledge.getStarters)
 *  - User / assistant bubbles with markdown-lite rendering
 *  - Thumbs up / down feedback + copy
 *  - Session persistence via sessionStorage (basky_session_v2)
 *  - "Expand to full page" button opens /training/assistant
 */
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Sparkles, Send, X, Maximize2, ThumbsUp, ThumbsDown, Copy, Loader2, MapPin, ArrowRight, RotateCcw, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getStarters } from './askBaskyKnowledge';
import api from '../../lib/api';

const SESSION_KEY = 'basky_session_v2';
const THREAD_KEY = 'basky_thread_v2';

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-slate-800">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const out = [];
  let listBuf = null;
  const flushList = () => {
    if (listBuf) {
      out.push(<ul key={`l-${out.length}`} className="list-disc pl-5 my-1 space-y-0.5">{listBuf}</ul>);
      listBuf = null;
    }
  };
  lines.forEach((ln, i) => {
    const trimmed = ln.trim();
    if (/^\d+\.\s/.test(trimmed)) {
      flushList();
      out.push(<p key={i} className="my-1"><span className="font-semibold text-violet-700">{trimmed.match(/^\d+/)[0]}. </span>{renderInline(trimmed.replace(/^\d+\.\s/, ''))}</p>);
    } else if (trimmed.startsWith('- ')) {
      listBuf = listBuf || [];
      listBuf.push(<li key={i}>{renderInline(trimmed.slice(2))}</li>);
    } else if (trimmed === '') {
      flushList();
      out.push(<div key={i} className="h-1" />);
    } else {
      flushList();
      out.push(<p key={i} className="my-1">{renderInline(trimmed)}</p>);
    }
  });
  flushList();
  return out;
}

export default function AskBaskyDrawer({ open, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [thread, setThread] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(THREAD_KEY) || '[]'); } catch { return []; }
  });
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(SESSION_KEY) || null);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [indexInfo, setIndexInfo] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const scrollRef = useRef(null);

  const starters = getStarters(location.pathname);

  useEffect(() => { sessionStorage.setItem(THREAD_KEY, JSON.stringify(thread)); }, [thread]);
  useEffect(() => { if (sessionId) sessionStorage.setItem(SESSION_KEY, sessionId); }, [sessionId]);

  useEffect(() => {
    if (!open) return;
    api.get('/ai/index').then(r => setIndexInfo(r.data)).catch(() => { /* ignore */ });
  }, [open]);

  useEffect(() => {
    if (!open || scrollRef.current == null) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread, typing, open]);

  const ask = async (q) => {
    const userMsg = { role: 'user', id: `u-${Date.now()}`, text: q, at: new Date().toISOString() };
    setThread(t => [...t, userMsg]);
    setInput('');
    setTyping(true);
    try {
      const r = await api.post('/ai/ask', { question: q, session_id: sessionId });
      if (r.data?.session_id && r.data.session_id !== sessionId) setSessionId(r.data.session_id);
      setThread(t => [...t, {
        role: 'assistant',
        id: `a-${Date.now()}`,
        text: r.data?.answer || "I couldn't produce an answer — please try again.",
        snapshot: r.data?.snapshot,
        at: new Date().toISOString(),
      }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Network error';
      setThread(t => [...t, {
        role: 'assistant',
        id: `a-${Date.now()}`,
        text: `**Something went wrong.** ${detail}`,
        error: true,
        at: new Date().toISOString(),
      }]);
    } finally {
      setTyping(false);
    }
  };

  const newChat = async () => {
    if (sessionId) {
      try { await api.delete(`/ai/session/${sessionId}`); } catch { /* ignore */ }
    }
    setThread([]);
    setSessionId(null);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(THREAD_KEY);
    toast.success('Started a new chat');
  };

  const reindex = async () => {
    setReindexing(true);
    try {
      const r = await api.post('/ai/reindex');
      setIndexInfo(r.data);
      const c = r.data?.counts || {};
      toast.success(`Knowledge refreshed · ${c.total_routes || 0} routes, ${c.frontend_files || 0} pages`);
      // Clear current session so the next turn picks up the refreshed catalog
      if (sessionId) {
        try { await api.delete(`/ai/session/${sessionId}`); } catch { /* ignore */ }
        setSessionId(null);
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to refresh knowledge');
    } finally {
      setReindexing(false);
    }
  };

  const submitFeedback = (msgId, kind) => {
    setFeedback(f => ({ ...f, [msgId]: kind }));
    toast.success(kind === 'up' ? 'Thanks! Glad it helped' : "Noted — we'll keep tuning Basky");
  };

  const copyAnswer = (text) => {
    navigator.clipboard.writeText(text.replace(/\*\*/g, ''));
    toast.success('Answer copied');
  };

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] p-0 flex flex-col" data-testid="ask-basky-drawer">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-semibold">Ask Basky</p>
              <p className="text-[10px] opacity-80 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                Online · Claude Sonnet 4.5
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={reindex} disabled={reindexing} title="Refresh knowledge from codebase" className="p-1.5 rounded hover:bg-white/20 transition-colors disabled:opacity-50" data-testid="ask-basky-reindex">
              <RefreshCw className={`w-4 h-4 ${reindexing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={newChat} title="New chat" className="p-1.5 rounded hover:bg-white/20 transition-colors" data-testid="ask-basky-new-chat">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button onClick={() => { onClose(); navigate('/training/assistant'); }} title="Expand to full page" className="p-1.5 rounded hover:bg-white/20 transition-colors">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} title="Close" className="p-1.5 rounded hover:bg-white/20 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Context strip */}
        <div className="px-4 py-2 border-b border-slate-100 bg-violet-50/30 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <MapPin className="w-3 h-3 text-violet-500 flex-shrink-0" />
            <span className="text-[10px] text-slate-500">On:</span>
            <code className="text-[10px] font-mono text-violet-700 truncate">{location.pathname}</code>
          </div>
          {indexInfo?.counts && (
            <span className="text-[10px] text-slate-400 whitespace-nowrap" title={`Catalog sig ${indexInfo.signature} · built ${indexInfo.built_at}`} data-testid="ask-basky-index-badge">
              {indexInfo.counts.total_routes} routes · {indexInfo.counts.frontend_files} pages
            </span>
          )}
        </div>

        {/* Thread */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {thread.length === 0 && (
            <WelcomeCard starters={starters} onStarter={ask} />
          )}
          {thread.map(m =>
            m.role === 'user'
              ? <UserBubble key={m.id} text={m.text} />
              : <AssistantBubble key={m.id} msg={m}
                  feedback={feedback[m.id]} onFeedback={submitFeedback}
                  onCopy={copyAnswer} />
          )}
          {typing && <TypingIndicator />}
        </div>

        {/* Input */}
        <div className="border-t bg-white p-3">
          <form onSubmit={(e) => { e.preventDefault(); if (input.trim() && !typing) ask(input.trim()); }}>
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask about any module..."
                disabled={typing}
                className="h-10 text-sm"
                data-testid="ask-basky-input"
              />
              <Button type="submit" disabled={!input.trim() || typing} className="h-10 bg-violet-600 hover:bg-violet-700" data-testid="ask-basky-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
          <p className="text-[10px] text-slate-400 mt-1.5 text-center italic">
            Powered by Claude Sonnet 4.5 · Answers grounded with live DB snapshot
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function WelcomeCard({ starters, onStarter }) {
  return (
    <div className="space-y-3" data-testid="ask-basky-welcome">
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="bg-violet-50 border border-violet-100 rounded-lg rounded-tl-none px-3 py-2 text-sm text-slate-700 leading-relaxed">
          <strong className="text-violet-700">Hi! I'm Basky.</strong> Ask me how any module works, or for live numbers from your data. Tap a suggestion or type your question.
        </div>
      </div>
      <div className="pl-10 space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Try asking</p>
        {starters.map((s, i) => (
          <button
            key={i}
            onClick={() => onStarter(s)}
            className="w-full text-left px-3 py-2 bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 rounded-md text-xs text-slate-700 transition-colors group flex items-center justify-between"
            data-testid={`ask-basky-starter-${i}`}
          >
            <span>{s}</span>
            <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
          </button>
        ))}
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] bg-slate-100 border border-slate-200 rounded-lg rounded-tr-none px-3 py-2 text-sm text-slate-800 leading-relaxed">
        {text}
      </div>
    </div>
  );
}

function AssistantBubble({ msg, feedback, onFeedback, onCopy }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`border rounded-lg rounded-tl-none px-3 py-2 text-sm leading-relaxed ${msg.error ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-white border-slate-200 text-slate-700'}`}>
          {renderMarkdown(msg.text)}
        </div>
        <div className="flex items-center gap-0.5 mt-1 pl-1">
          <button onClick={() => onFeedback(msg.id, 'up')} className={`p-1 rounded hover:bg-slate-100 ${feedback === 'up' ? 'text-emerald-600' : 'text-slate-400'}`} title="Helpful">
            <ThumbsUp className="w-3 h-3" />
          </button>
          <button onClick={() => onFeedback(msg.id, 'down')} className={`p-1 rounded hover:bg-slate-100 ${feedback === 'down' ? 'text-rose-600' : 'text-slate-400'}`} title="Not helpful">
            <ThumbsDown className="w-3 h-3" />
          </button>
          <button onClick={() => onCopy(msg.text)} className="p-1 rounded hover:bg-slate-100 text-slate-400" title="Copy">
            <Copy className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg rounded-tl-none px-4 py-3">
        <div className="flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
      </div>
    </div>
  );
}
